import {
  DocumentClassAttributes,
  MastersUserAttributes,
  OrderAttributes,
} from "@albatrosdeveloper/ave-models-package";
import {
  OrderOperationTypeEnum,
  StatusEnum,
} from "@albatrosdeveloper/ave-models-package/lib/enums";
import {
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { DocumentTypeEnum } from "app/common/enums/document-types.enum";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { ApiResponseDataImport } from "app/shared/components/excel-import/components/import-dialog/import-dialog.component";
import { PreValidationResponse } from "app/shared/components/excel-import/interfaces/pre-validation.interface";
import { environment } from "environments/environment.qas";
import { Observable, of } from "rxjs";
import { environment as env } from "../../../../../environments/environment.prod";
import {
  IAPIItemResult,
  IAPIListResult,
  IPagination,
  IPaginationResultFrequencies,
  IRequestResult,
} from "../../../../common/interfaces";
import { AbstractService } from "../../../../services/abstract.service";

const apiUrl = env.API_AVE_ORDER_URL;
const moduleName = "order";
const documentUrl = env.API_DOCUMENT_URL + "api/";
const masterUrl = env.API_UNIT_MEASURE + "api/";
const integratorUrl = env.API_AVE_INTEGRATOR_URL + "api/";
const pathOrderIntegrator = env.PATH_ORDER_INTEGRATOR;
const businessPartnerUrl = env.API_BUSINESS_PARTNER + "api/";

@Injectable({
  providedIn: "root",
})
export class OrdersListService extends AbstractService<
  OrderAttributes,
  Partial<OrderAttributes>,
  Partial<OrderAttributes>
> {
  currentFilter: CombinedFilter<OrderAttributes>;
  currentFilterData: any;

  constructor(http: HttpClient) {
    super(apiUrl, moduleName, http);
  }

  findByFilter(
    filter: CombinedFilter<OrderAttributes>,
    filterData: any,
    options?: RequestOptions
  ) {
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCountV2?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    const headers: Record<string, any> = {};
    // see order-customer-export for more info about this header
    // ctx: stands for context
    if (options?.contextHeader) headers["x-ave-ctx"] = options.contextHeader;
    return this.http.post<IPaginationResultFrequencies<OrderAttributes>>(
      API_URL,
      filterData,
      { headers }
    );
  }

  /**
   * Pagination
   * @param options Pagination options
   * @returns Paginated data
   */
  findAllWithCount(
    options: IPagination<OrderAttributes>,
    dataToFilter?
  ): Observable<IPaginationResultFrequencies<OrderAttributes>> {
    const hasSearch = !!options.search;

    const buildBaseFilters = (params: {
      useOrWhere: boolean;
    }): FilterFn<OrderAttributes>[] => {
      const filterFn = params.useOrWhere ? orWhere : andWhere;

      return [
        filterFn("_deleted", Ops.not(true)),
        andWhere(
          "company.id" as keyof OrderAttributes,
          Ops.in(...[environment.companyId])
        ),
        // 5 filtros de fechas
        dataToFilter.startDate && dataToFilter.endDate
          ? andWhere(
              "creationDateTime",
              Ops.btwnDates(dataToFilter.startDate, dataToFilter.endDate)
            )
          : noop(),

        dataToFilter.startTrip && dataToFilter.endTrip
          ? andWhere(
              "trip.dateHourStartTeoric" as keyof OrderAttributes,
              Ops.btwnDates(dataToFilter.startTrip, dataToFilter.endTrip)
            )
          : noop(),

        dataToFilter.end_startTrip && dataToFilter.end_endTrip
          ? andWhere(
              "trip.dateHourEndTeoric" as keyof OrderAttributes,
              Ops.btwnDates(
                dataToFilter.end_startTrip,
                dataToFilter.end_endTrip
              )
            )
          : noop(),

        dataToFilter.startDeliveryDate && dataToFilter.endDeliveryDate
          ? andWhere(
              "deliveryDateTime",
              Ops.btwnDates(
                dataToFilter.startDeliveryDate,
                dataToFilter.endDeliveryDate
              )
            )
          : noop(),

        dataToFilter.pickerUpload_start && dataToFilter.pickerUpload_end
          ? andWhere(
              "loadDateTime",
              Ops.btwnDates(
                dataToFilter.pickerUpload_start,
                dataToFilter.pickerUpload_end
              )
            )
          : noop(),

        // Filtro especial: ordersToFilter (solo cuando NO hay búsqueda)
        dataToFilter.ordersToFilter &&
        dataToFilter.ordersToFilter.length > 0 &&
        !hasSearch
          ? andWhere("serial", Ops.in(...dataToFilter.ordersToFilter))
          : noop(),

        // Filtros condicionales únicos
        dataToFilter.type
          ? andWhere(<any>"type.code", Ops.eq(dataToFilter.type))
          : noop(),

        dataToFilter.withTravel == "1"
          ? andWhere("trip", Ops.not(null))
          : dataToFilter.withTravel == "2"
            ? andWhere("trip", null)
            : noop(),

        // 11 filtros de arrays
        dataToFilter.documentClass && dataToFilter.documentClass.length > 0
          ? andWhere(<any>"class.code", Ops.in(...dataToFilter.documentClass))
          : noop(),

        dataToFilter.seller && dataToFilter.seller.length > 0
          ? andWhere(
              <any>"user.user_login.username",
              Ops.in(...dataToFilter.seller)
            )
          : noop(),

        dataToFilter.statusERP &&
        dataToFilter.statusERP.length > 0 &&
        !dataToFilter.statusERP.includes(null)
          ? andWhere("statusERP", Ops.in(...dataToFilter.statusERP))
          : noop(),

        dataToFilter.status &&
        dataToFilter.status.length > 0 &&
        !dataToFilter.status.includes(null)
          ? andWhere("statusId", Ops.in(...dataToFilter.status))
          : noop(),

        dataToFilter.typeCustomer &&
        dataToFilter.typeCustomer.length > 0 &&
        !dataToFilter.typeCustomer.includes(null)
          ? andWhere(
              "shipTo.types.code" as keyof OrderAttributes,
              Ops.in(...dataToFilter.typeCustomer)
            )
          : noop(),

        dataToFilter.materialGroupId &&
        dataToFilter.materialGroupId.length > 0 &&
        !dataToFilter.materialGroupId.includes(null)
          ? andWhere(
              "detail.material.materialGroups.code" as keyof OrderAttributes,
              Ops.in(...dataToFilter.materialGroupId)
            )
          : noop(),

        dataToFilter.materialId &&
        dataToFilter.materialId.length > 0 &&
        !dataToFilter.materialId.includes(null)
          ? andWhere(
              "detail.material.code" as keyof OrderAttributes,
              Ops.in(...dataToFilter.materialId)
            )
          : noop(),

        dataToFilter.soldTo && dataToFilter.soldTo.length > 0
          ? andWhere(<any>"soldTo.id", Ops.in(...dataToFilter.soldTo))
          : noop(),

        dataToFilter.shipTo && dataToFilter.shipTo.length > 0
          ? andWhere(<any>"shipTo.id", Ops.in(...dataToFilter.shipTo))
          : noop(),

        dataToFilter.salesOfficeId && dataToFilter.salesOfficeId.length > 0
          ? andWhere(
              "salesOffice.id" as keyof OrderAttributes,
              Ops.in(...dataToFilter.salesOfficeId)
            )
          : noop(),

        dataToFilter.warehouseId && dataToFilter.warehouseId.length > 0
          ? andWhere(
              "warehouses.id" as keyof OrderAttributes,
              Ops.in(...dataToFilter.warehouseId)
            )
          : noop(),

        dataToFilter.subRouteId && dataToFilter.subRouteId.length > 0
          ? andWhere(
              "subRoute.id" as keyof OrderAttributes,
              Ops.in(...dataToFilter.subRouteId)
            )
          : noop(),

        dataToFilter.tripId
          ? andWhere(
              "trip.id" as keyof OrderAttributes,
              Ops.eq(dataToFilter.tripId)
            )
          : noop(),

        dataToFilter.operationType
          ? andWhere("operationType", Ops.eq(dataToFilter.operationType))
          : noop(),
      ];
    };

    // Objeto 1: Búsqueda por serial (número de pedido)
    const searchBySerialQueryObject: FilterFn<OrderAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),
      hasSearch ? andWhere("serial", Ops.like(options.search!, "i")) : noop(),
    ];

    // Objeto 2: Búsqueda por nombre del cliente (soldTo)
    const searchBySoldToNameQueryObject: FilterFn<OrderAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "soldTo.name" as keyof OrderAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    // Objeto 3: Búsqueda por nombre del destinatario (shipTo)
    const searchByShipToNameQueryObject: FilterFn<OrderAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "shipTo.name" as keyof OrderAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    // Objeto 4: Búsqueda por username del vendedor
    const searchByUsernameQueryObject: FilterFn<OrderAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "user.user_login.username" as keyof OrderAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    // Objeto 5: Búsqueda por nombre de ruta
    const searchBySubRouteNameQueryObject: FilterFn<OrderAttributes>[] =
      hasSearch
        ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere(
              "subRoute.name" as keyof OrderAttributes,
              Ops.like(options.search!, "i")
            ),
          ]
        : [];

    const query = buildQuery<OrderAttributes>(
      ...searchBySerialQueryObject,
      ...searchBySoldToNameQueryObject,
      ...searchByShipToNameQueryObject,
      ...searchByUsernameQueryObject,
      ...searchBySubRouteNameQueryObject,
      orderBy(
        options.sort ?? "createdAt",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string = apiUrl + `api/${moduleName}/withCountV3`;
    return this.http.post<IPaginationResultFrequencies<OrderAttributes>>(
      API_URL,
      {
        filter: query,
      }
    );
  }

  findAllDownloadWithCount(
    options: IPagination<OrderAttributes>,
    dataToFilter?
  ): Observable<IPaginationResultFrequencies<OrderAttributes>> {
    let dataToSend = {
      search: options.search,
    };

    const startDate = new Date(
      new Date(dataToFilter.creationDateTime).setHours(0, 0, 0, 0)
    );
    const endDate = new Date(
      new Date(dataToFilter.creationDateTime).setHours(23, 59, 59, 999)
    );

    const arrayQueries: FilterFn<OrderAttributes>[] = [];
    if (dataToFilter.type) {
      arrayQueries.push(
        andWhere(
          "subRoute.id" as keyof OrderAttributes,
          Ops.eq(dataToFilter.route)
        )
      );
    }

    const query = buildQuery<OrderAttributes>(
      where(<any>"company.id", Ops.in(...[environment.companyId])),
      andWhere(<any>"type.code", Ops.eq(dataToFilter.type)),
      andWhere("creationDateTime", Ops.btwnDates(startDate, endDate)),
      andWhere(
        "salesOffice.id" as keyof OrderAttributes,
        Ops.eq(dataToFilter.office)
      ),
      ...arrayQueries,
      andWhere("_deleted", Ops.not(true)),
      orderBy(
        options.sort ?? "creationDateTime",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCountV2?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.post<IPaginationResultFrequencies<OrderAttributes>>(
      API_URL,
      dataToSend
    );
  }

  fetchDocumentClasses(
    filter?
  ): Observable<IAPIListResult<DocumentClassAttributes>> {
    const query = buildQuery<DocumentClassAttributes>(
      where("_deleted", Ops.eq(false)),
      andWhere("statusId", StatusEnum.ACTIVE),
      filter && filter.type
        ? andWhere(<any>"documentType.code", Ops.eq(filter.type))
        : noop()
    );
    let API_URL: string = `${documentUrl}document_class/filter`;
    if (query) {
      API_URL += `?filter=${encodeURIComponent(JSON.stringify(query))}`;
    }
    return this.http.get<IAPIListResult<DocumentClassAttributes>>(API_URL);
  }

  fetchMasterUsers(): Observable<IAPIListResult<any>> {
    const query = buildQuery<MastersUserAttributes>(
      where("applications_ids", Ops.in(...[environment.applicationId])),
      andWhere(
        "sub_applications_ids",
        Ops.in(...[environment.subApplicationId_csmobile])
      )
    );

    let API_URL: string = masterUrl + "master_user/user";
    if (query) {
      API_URL += `?filter=${encodeURIComponent(JSON.stringify(query))}`;
    }
    return this.http.get<IAPIListResult<any>>(API_URL);
  }

  //Liberar pedidos
  releaseOrders(data: { orderSerials: string[] }) {
    let API_URL: string = apiUrl + "/api/order/release_orders_to_send_erp";
    return this.http.post(API_URL, data);
  }

  // Para sincronizar pedidos y documentos de carga
  synchronizeOrders(dataToSend) {
    if (!pathOrderIntegrator) {
      console.warn("No se construyó API porque pathOrderIntegrator es null");
      return of(null);
    }

    let API_URL: string =
      integratorUrl + pathOrderIntegrator + (env.PATH_SEND_ERP ?? "");
    return this.http.post(API_URL, dataToSend);
  }

  // Para sincronizar facturas, mermas y devoluciones
  synchronizeOrdersOther(dataToSend) {
    if (!pathOrderIntegrator) {
      console.warn("No se construyó API porque pathOrderIntegrator es null");
      return of(null);
    }

    let API_URL: string =
      integratorUrl + pathOrderIntegrator + "/send_documents_to_erp";
    return this.http.post(API_URL, dataToSend);
  }

  getListInvoices(dataToSend) {
    if (!pathOrderIntegrator) {
      console.warn("No se construyó API porque pathOrderIntegrator es null");
      return of(null);
    }

    let API_URL: string = integratorUrl + pathOrderIntegrator + "/get-fac";
    return this.http.post(API_URL, dataToSend);
  }

  synchronizeOrdersOtherV2(dataToSend) {
    if (!pathOrderIntegrator) {
      console.warn("No se construyó API porque pathOrderIntegrator es null");
      return of(null);
    }

    let API_URL: string =
      integratorUrl + pathOrderIntegrator + "/sync-order-return";
    return this.http.post(API_URL, dataToSend);
  }

  // Para generar documento de transporte
  generateDocumentTransport(dataToSend) {
    const API_URL: string = apiUrl + `api/${moduleName}/generate_tdoc`;
    return this.http.post(API_URL, dataToSend);
  }

  // Para anular documentos de transporte
  annularDocumentTransport(
    id: string,
    dataToSend
  ): Observable<IRequestResult<OrderAttributes>> {
    const API_URL: string = apiUrl + `api/${moduleName}/tdoc/${id}`;
    return this.http.patch<IRequestResult<OrderAttributes>>(
      API_URL,
      dataToSend
    );
  }

  updateV2(id: string, data): Observable<IRequestResult<OrderAttributes>> {
    let API_URL: string = apiUrl + `api/${moduleName}/v2/${id}`;
    return this.http.patch<IRequestResult<OrderAttributes>>(API_URL, data);
  }

  approveNextDocument(id, dataToSend) {
    let API_URL: string = apiUrl + `api/order/approve_generate_next/${id}`;
    return this.http.post(API_URL, dataToSend);
  }

  getFileTemplateDeliver(): string {
    return env.DELIV_ROUTE_LOAD_TEMPLATE || null;
  }

  importFile(data: FormData) {
    const API_URL: string = apiUrl + `api/${moduleName}/uploadxls`;
    return this.http.post(API_URL, data);
  }

  fetchSoldTos(dataToSend): Observable<any> {
    let API_URL: string =
      businessPartnerUrl + `/business-partner/get_all_soldtos`;
    return this.http.post<IAPIItemResult<any>>(API_URL, dataToSend);
  }

  fetchShipTos(dataToSend): Observable<any> {
    let API_URL: string =
      businessPartnerUrl + `/business-partner/get_all_shiptos`;
    return this.http.post<IAPIItemResult<any>>(API_URL, dataToSend);
  }

  updateNoteInvoice(
    id: string,
    data
  ): Observable<IRequestResult<OrderAttributes>> {
    let API_URL: string = apiUrl + `api/${moduleName}/update_note/${id}`;
    return this.http.patch<IRequestResult<OrderAttributes>>(API_URL, data);
  }

  findToLoadDocument(
    serial: string
  ): Observable<IRequestResult<OrderAttributes[]>> {
    const query = buildQuery<OrderAttributes>(
      where("_deleted", Ops.eq(false)),
      serial
        ? andWhere(
            "sourceTransactions.serial" as keyof OrderAttributes,
            Ops.eq(serial)
          )
        : noop(),
      andWhere(
        "type.code" as keyof OrderAttributes,
        Ops.eq(DocumentTypeEnum.DOCUMENTO_DE_CARGA)
      ),
      select(["serial"])
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<OrderAttributes[]>>(API_URL);
  }

  // TODO: Implement this method
  getOrderTemplateUrl(): string {
    return env.ORDERS_IMPORT_TEMPLATE_URL;
  }

  importOrdersFromExcel(formData: FormData): Observable<ApiResponseDataImport> {
    const API_URL: string = apiUrl + `api/order_import/upload/orders`;
    return this.http.post<ApiResponseDataImport>(API_URL, formData);
  }

  /**
   * Pre-valida un archivo Excel de pedidos antes de la importación final
   * @param formData FormData con el archivo Excel a validar
   * @returns Observable con el resultado de la pre-validación
   */
  preValidateOrdersFromExcel(
    formData: FormData
  ): Observable<PreValidationResponse> {
    const API_URL: string = apiUrl + `api/order_import/pre-validate`;
    return this.http.post<PreValidationResponse>(API_URL, formData);
  }

  findToLoadDocumentUnified(
    serial: string
  ): Observable<IRequestResult<OrderAttributes[]>> {
    const query = buildQuery<OrderAttributes>(
      where("_deleted", Ops.eq(false)),
      andWhere("operationType", Ops.eq(OrderOperationTypeEnum.ORDER_JOIN)),
      serial
        ? andWhere(
            "sourceTransactions.serial" as keyof OrderAttributes,
            Ops.eq(serial)
          )
        : noop(),
      andWhere(
        "type.code" as keyof OrderAttributes,
        Ops.eq(DocumentTypeEnum.DOCUMENTO_DE_CARGA)
      ),
      select(["serial"])
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<OrderAttributes[]>>(API_URL);
  }
}
